import React, { RefObject, forwardRef } from 'react';
import classNames from 'classnames';
import { animated, useSpring } from '@react-spring/web';
import { interpolate as d3interpolate } from 'd3';

import { Comp, useId } from '@redsift/design-system';

import { ArcsProps } from './types';
import { StyledArcs } from './styles';
import { Arc } from '../Arc';
import { config } from '../../config';

const COMPONENT_NAME = 'Arcs';
const CLASSNAME = 'redsift-arcs';

export const Arcs: Comp<ArcsProps, SVGGElement> = forwardRef((props, ref) => {
  const {
    arcs,
    className,
    color,
    disableAnimations,
    hasLabels,
    hasStroke,
    id: propsId,
    labelDecorator,
    onClick,
    role,
    sliceProps,
    tooltipVariant,
    ...forwardedProps
  } = props;
  const [_id] = useId();
  const id = propsId ?? _id;

  const animatedProps = useSpring({
    ...config,
    to: async (next: (props?: object) => Promise<void>) => {
      await next({ t: 1 });
    },
    from: { t: 0 },
  });

  return (
    <StyledArcs
      {...forwardedProps}
      className={classNames(Arcs.className, className)}
      ref={ref as RefObject<SVGGElement>}
    >
      <g className="arc-group">
        {arcs.map((arc, index) => {
          const arcIndex = arc.index ?? index;
          const arcId = arc.id ?? `id${id}__arc-${arcIndex}`;

          return (
            <Arc
              {...sliceProps}
              color={color}
              disableAnimations={disableAnimations}
              hasStroke={hasStroke}
              labelDecorator={labelDecorator}
              onClick={onClick}
              role={role}
              tooltipVariant={tooltipVariant}
              {...arc}
              key={arcId}
              id={arcId}
              index={arcIndex}
            />
          );
        })}
      </g>
      {hasLabels ? (
        <g className="arc-label-group">
          {arcs
            .filter((arc) => {
              const value = arc.data.data.value;
              const percent = arc.data.data.percent;
              return percent ? value > 0 && percent >= 0.1 : value > 0;
            })
            .map((arc, index) => {
              const arcIndex = arc.index ?? index;
              const arcId = arc.id ?? `id${id}__arc-${arcIndex}`;
              const arcLabelDecorator = arc.labelDecorator ?? labelDecorator;
              const interpolator = d3interpolate(arc.from, arc.data);

              return (
                <animated.text
                  alignmentBaseline="middle"
                  className={`arc-label _${index}`}
                  key={`${arcId}-label`}
                  textAnchor="middle"
                  transform={
                    config.immediate || disableAnimations
                      ? `translate(${arc.createArc.centroid(arc.data).map((n) => n.toFixed(4))})`
                      : animatedProps.t.to(
                          (t) => `translate(${arc.createArc.centroid(interpolator(t)).map((n) => n.toFixed(4))})`
                        )
                  }
                >
                  {arcLabelDecorator ? arcLabelDecorator(arc.data) : arc.data.data.key}
                </animated.text>
              );
            })}
        </g>
      ) : null}
    </StyledArcs>
  );
});
Arcs.className = CLASSNAME;
Arcs.displayName = COMPONENT_NAME;
